import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { IconId, Spacer } from "src/components";
import { Tab } from "src/components/tab";
import { Item } from "src/models";
import {
  AuthStore, DeliverableStore, ItemStore, QuestionStore, SpaceStore
} from "src/stores";
import { DataType, IItem } from "src/types";
import styled from "styled-components";
import { EditItem } from "../room/editData";
import { Row } from "../room/editData/gridElements";
import { TabDataPanel } from "./tabDataPanel";

interface IItemsBoardProps {
  authStore?:  AuthStore;
  itemStores?: { [type: string]: ItemStore<Item<IItem>, IItem> }; // This is injected so it has to be called match
  spaceStore?: SpaceStore;
}

export const ItemsBoard = inject("itemStores", "userStore", "spaceStore", "authStore")(observer((props: IItemsBoardProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { itemStores, authStore, spaceStore } = props;

  const [type,   setType]   = React.useState(DataType.Question);
  const [tabIdx, setTabIdx] = React.useState<number>(0);

  const getStorage = React.useCallback(
    (idx: number, variant: "priority" | "critical" | "board") => {
      if (!idx) return DeliverableStore.ContainerType.Priority;
      else if (idx === 1)
        return variant === "critical"
          ? QuestionStore.ContainerType.ToCrit
          : QuestionStore.ContainerType.ToPrio;
      else
        return variant === "critical"
          ? QuestionStore.ContainerType.FromCrit
          : QuestionStore.ContainerType.FromPrio;
      },
    []
  );
  
  const tabs: { title: string; type: DataType; icon: IconId }[] = [
    {
      title: t("deliverables"),
      type:  DataType.Deliverable,
      icon:  "schedule"
    },{
      title: t("questions_plural_received"),
      type:  DataType.Question,
      icon:  "receive"
    },{
      title: t("questions_plural_sent"),
      type:  DataType.Question,
      icon:  "dispatch"
    }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Row style={{ flex: 1 }}>
        {tabs.map((tab, idx) => (
          <StyledTab
            key     = {tab.title}
            active  = {tabIdx === idx}
            title   = {tab.title}
            iconId  = {tab.icon}
            onClick = {() => {
              setType(tab.type);
              setTabIdx(idx);
            }}
          />
        ))}
      </Row>
      <Spacer size="small" />
      {!tabIdx ? null : (
        <TabDataPanel
          variant = "critical"
          toggle  = {Boolean(tabIdx)}
          type    = {tabIdx ? DataType.Question : DataType.Deliverable}
          storage = {
            tabIdx
              ? itemStores[DataType.Question].storage[
                  getStorage(tabIdx, "critical")
                ]
              : undefined
          }
        />
      )}
      <TabDataPanel
        variant = "priority"
        toggle  = {Boolean(tabIdx)}
        type    = {tabIdx ? DataType.Question : DataType.Deliverable}
        storage = {
          itemStores[tabIdx ? DataType.Question : DataType.Deliverable]
            .storage[getStorage(tabIdx, "priority")]
        }
      />
      <TabDataPanel
        variant = "others"
        toggle  = {Boolean(tabIdx)}
        type    = {tabIdx ? DataType.Question : DataType.Deliverable}
        storage = {
          itemStores[tabIdx ? DataType.Question : DataType.Deliverable]
            .storage[tabIdx ? tabIdx == 1 ? QuestionStore.ContainerType.From : QuestionStore.ContainerType.To : DeliverableStore.ContainerType.Next]
        }
      />
      <EditItem />
    </div>
  );
}));

const StyledTab = styled(Tab)`
  margin-right: 0.5rem;
`;
